/** Same 6-month window: page vs typical SERP date must be within this gap to count as "similar". */
export const FRESHNESS_SIX_MONTHS_MS = 183 * 24 * 60 * 60 * 1000;
export const FRESHNESS_TWO_YEARS_MS = 730 * 24 * 60 * 60 * 1000;

export type FreshnessSerpRelation = 'similar' | 'newer' | 'older' | 'unknown';

/**
 * Every badge the Freshness card can show. Behind / In line / Ahead are read off
 * the SERP comparison and nothing else; Stale / Aging / Recent exist only for a
 * page with no SERP dates to compare against, and their sentence says so.
 * TECHNICAL_DEBT §0bg: the badge used to fall back to page age inside the band,
 * so a page newer than the SERP read "Aging" beside "similar freshness band".
 */
export const FRESHNESS_LABEL = {
  noDate: 'No date',
  behindSerp: 'Behind SERP',
  inLineWithSerp: 'In line with SERP',
  aheadOfSerp: 'Ahead of SERP',
  stale: 'Stale',
  aging: 'Aging',
  recent: 'Recent'
} as const;

export type FreshnessDiagnosisLabel = (typeof FRESHNESS_LABEL)[keyof typeof FRESHNESS_LABEL];

type PageAgeBand = 'stale' | 'aging' | 'recent';

function pageAgeBand(pageDate: Date, nowMs: number): PageAgeBand {
  const ageMs = nowMs - pageDate.getTime();
  if (ageMs >= FRESHNESS_TWO_YEARS_MS) return 'stale';
  if (ageMs >= FRESHNESS_SIX_MONTHS_MS) return 'aging';
  return 'recent';
}

const MS_PER_MONTH = (365.25 / 12) * 24 * 60 * 60 * 1000;

/** "6 months" / "2 years", read off the constant so the copy cannot drift from the rule. */
function spanLabel(ms: number): string {
  const months = Math.round(ms / MS_PER_MONTH);
  if (months % 12 === 0) return `${months / 12} year${months === 12 ? '' : 's'}`;
  return `${months} months`;
}

export interface FreshnessSerpCompareResult {
  relation: FreshnessSerpRelation;
  serpTypicalDate: Date | null;
  serpTypicalDateLabel: string | null;
  verdict: string | null;
}

export function medianPublishedDate(sortedDates: Date[]): Date | null {
  if (!sortedDates.length) return null;
  const mid = Math.floor(sortedDates.length / 2);
  if (sortedDates.length % 2 === 1) return sortedDates[mid]!;
  return new Date((sortedDates[mid - 1]!.getTime() + sortedDates[mid]!.getTime()) / 2);
}

export function formatFreshnessDateLabel(d: Date): string {
  try {
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

/** Compare crawl last-modified vs typical top-SERP publication date (median of dated winners). */
export function comparePageFreshnessToSerp(
  pageDate: Date | null,
  serpDates: Date[],
  nowMs = Date.now()
): FreshnessSerpCompareResult {
  const sorted = [...serpDates].sort((a, b) => a.getTime() - b.getTime());
  const median =
    sorted.length >= 2
      ? medianPublishedDate(sorted)
      : sorted.length === 1
        ? sorted[0]!
        : null;
  const serpTypicalDateLabel = median ? formatFreshnessDateLabel(median) : null;

  if (!pageDate && !median) {
    return { relation: 'unknown', serpTypicalDate: null, serpTypicalDateLabel: null, verdict: null };
  }
  if (!pageDate && median) {
    return {
      relation: 'unknown',
      serpTypicalDate: median,
      serpTypicalDateLabel,
      verdict: 'Compare your last content update when you have a crawl date.'
    };
  }
  if (pageDate && !median) {
    return {
      relation: 'unknown',
      serpTypicalDate: null,
      serpTypicalDateLabel: null,
      verdict: pageAgeOnlyVerdict(pageDate, nowMs)
    };
  }

  // Compare calendar gap between your last update and typical SERP date (not “both old from today”).
  const pageToSerpGapMs = median!.getTime() - pageDate!.getTime();

  if (pageToSerpGapMs >= FRESHNESS_SIX_MONTHS_MS) {
    return {
      relation: 'older',
      serpTypicalDate: median,
      serpTypicalDateLabel,
      verdict: 'Your page looks older than typical top results.'
    };
  }
  if (pageToSerpGapMs <= -FRESHNESS_SIX_MONTHS_MS) {
    return {
      relation: 'newer',
      serpTypicalDate: median,
      serpTypicalDateLabel,
      verdict: 'Your page looks newer than typical top results.'
    };
  }
  // Inside the band the direction still shows: a page four months newer than the
  // SERP must not read the same as one four months older.
  return {
    relation: 'similar',
    serpTypicalDate: median,
    serpTypicalDateLabel,
    verdict:
      pageToSerpGapMs > 0
        ? `Your page is a little older than typical top results, but within ${spanLabel(FRESHNESS_SIX_MONTHS_MS)} of them.`
        : pageToSerpGapMs < 0
          ? `Your page is a little newer than typical top results, by less than ${spanLabel(FRESHNESS_SIX_MONTHS_MS)}.`
          : 'Your page was updated around the same time as typical top results.'
  };
}

function pageAgeOnlyVerdict(pageDate: Date, nowMs: number): string {
  const lead = 'Top results show too few publication dates to compare against, so this is based on your page\u2019s age alone:';
  const band = pageAgeBand(pageDate, nowMs);
  if (band === 'stale') return `${lead} last updated more than ${spanLabel(FRESHNESS_TWO_YEARS_MS)} ago.`;
  if (band === 'aging') return `${lead} last updated more than ${spanLabel(FRESHNESS_SIX_MONTHS_MS)} ago.`;
  return `${lead} updated within the last ${spanLabel(FRESHNESS_SIX_MONTHS_MS)}.`;
}

export interface SerpPublishedDateRow {
  position: number;
  publishedAt?: string | null;
}

export function serpWinnerPublishedDatesFromResults(
  results: SerpPublishedDateRow[] | null | undefined
): Date[] {
  const dates: Date[] = [];
  for (const row of results || []) {
    if (row.position < 1 || row.position > 7) continue;
    if (!row.publishedAt) continue;
    try {
      const d = new Date(row.publishedAt);
      if (!Number.isNaN(d.getTime())) dates.push(d);
    } catch {
      /* skip invalid */
    }
  }
  return dates;
}

export function parsePageContentLastModifiedDate(raw: unknown): Date | null {
  if (!raw) return null;
  const d = raw instanceof Date ? raw : new Date(String(raw));
  return Number.isNaN(d.getTime()) ? null : d;
}

export function pageContentLastModifiedLabel(pageDate: Date | null, rawFallback?: unknown): string {
  if (!pageDate) {
    if (rawFallback != null && rawFallback !== '') return String(rawFallback);
    return 'No last-update date on crawl';
  }
  return formatFreshnessDateLabel(pageDate);
}

export function freshnessDiagnosisLabel(
  pageDate: Date | null,
  serpDates: Date[],
  nowMs = Date.now()
): FreshnessDiagnosisLabel {
  if (!pageDate) return FRESHNESS_LABEL.noDate;
  const compare = comparePageFreshnessToSerp(pageDate, serpDates, nowMs);
  if (compare.relation === 'older') return FRESHNESS_LABEL.behindSerp;
  if (compare.relation === 'newer') return FRESHNESS_LABEL.aheadOfSerp;
  if (compare.relation === 'similar') return FRESHNESS_LABEL.inLineWithSerp;
  const band = pageAgeBand(pageDate, nowMs);
  if (band === 'stale') return FRESHNESS_LABEL.stale;
  if (band === 'aging') return FRESHNESS_LABEL.aging;
  return FRESHNESS_LABEL.recent;
}

export type FreshnessDiagnosisTone = 'warn' | 'ok' | 'muted' | 'neutral';

export function freshnessDiagnosisTone(label: string): FreshnessDiagnosisTone {
  if (
    label === FRESHNESS_LABEL.stale ||
    label === FRESHNESS_LABEL.behindSerp ||
    label === FRESHNESS_LABEL.aging
  ) {
    return 'warn';
  }
  if (label === FRESHNESS_LABEL.noDate) return 'muted';
  if (
    label === FRESHNESS_LABEL.aheadOfSerp ||
    label === FRESHNESS_LABEL.inLineWithSerp ||
    label === FRESHNESS_LABEL.recent
  ) {
    return 'ok';
  }
  return 'neutral';
}
